import * as React from 'react';

import type {WidgetFragment as Widget} from 'nutshell-graphql-types';

import {appHistory} from '../../../../../history';
import {CollapsibleCardWidget} from '../../../../core/card/collapsible-card-widget';

import {useGetActivitiesForWidget} from '../graphql/hooks/use-get-activities-for-widget';
import {
    setupActivityEventListeners,
    removeActivityEventListeners,
    getActivities,
    getEmptyStateProps,
    getIsTabLoading,
    getTabProps,
} from '../helpers';

import {ActivitiesList} from './activities-list';

type Props = {
    entityId: string;
    widget: Widget;
};

export function ActivitiesWidget(props: Props) {
    const [selectedTab, setSelectedTab] = React.useState<string>('scheduled');

    const {
        activities: loggedActivities,
        isFirstLoad: isLoggedActivitiesLoading,
        refetch: refetchLoggedActivities,
    } = useGetActivitiesForWidget(props.entityId, 1);

    const {
        activities: scheduledActivities,
        isFirstLoad: isScheduledActivitiesLoading,
        refetch: refetchScheduledActivities,
    } = useGetActivitiesForWidget(props.entityId, 0);

    // Set up event listener so if an activity is created while this component
    // is mounted (meaning the user is on a lead page), we will refetch
    // activities
    React.useEffect(() => {
        const handleActivityChange = () => {
            refetchScheduledActivities();
            refetchLoggedActivities();
        };

        setupActivityEventListeners(handleActivityChange);

        return () => {
            removeActivityEventListeners(handleActivityChange);
        };
    }, [refetchScheduledActivities, refetchLoggedActivities]);

    const handleAddActivity = React.useCallback(() => {
        setSelectedTab('scheduled');
        appHistory.replace({
            ...appHistory.location,
            hash: `/create/activities/${props.entityId}`,
            state: {modal: true},
        });
    }, [props.entityId]);

    const tabProps = React.useMemo(
        () => getTabProps(scheduledActivities, loggedActivities, selectedTab, setSelectedTab),
        [selectedTab, scheduledActivities, loggedActivities]
    );

    const emptyStateProps = React.useMemo(
        () => getEmptyStateProps(scheduledActivities, loggedActivities, selectedTab),
        [scheduledActivities, loggedActivities, selectedTab]
    );

    const isLoading = React.useMemo(
        () => getIsTabLoading(isScheduledActivitiesLoading, isLoggedActivitiesLoading, selectedTab),
        [selectedTab, isScheduledActivitiesLoading, isLoggedActivitiesLoading]
    );

    const activities = React.useMemo(() => {
        return getActivities(scheduledActivities, loggedActivities, selectedTab);
    }, [selectedTab, scheduledActivities, loggedActivities]);

    const scheduledActivitiesCount =
        scheduledActivities && scheduledActivities.length ? scheduledActivities.length : 0;

    return (
        <CollapsibleCardWidget
            widget={props.widget}
            emptyStateProps={emptyStateProps}
            isLoading={isLoading}
            addButtonProps={{
                variant: 'singular',
                onClick: handleAddActivity,
                label: '+ Schedule activity',
            }}
            tabProps={{tabs: tabProps}}
            count={scheduledActivitiesCount}
            hasBorder={true}
        >
            <ActivitiesList activities={activities} />
        </CollapsibleCardWidget>
    );
}
